import { memo, useRef, useState } from 'react';
import {
  MY_ACTIVITY_PAGE,
  TOURNAMENT_QUICK_CREATE_PAGE,
  getMyTournamentDashboardUrl,
} from 'constants/pages';
import { GetHostedEventsByUserIdQuery } from 'types/generated/client';
import { Position } from 'context/GeoLocationContext';
import { getImageUrl } from 'services/client/cloudflare/getImageUrl';
import { calculateHaversineDistance } from 'utils/shared/geo/calculateHaversineDistance';
import { useModal } from 'hooks/useModal';
import ChevronLeft from 'svg/ChevronLeft';
import ChevronRight from 'svg/ChevronRight';
import PlusPlain from 'svg/PlusPlain';
import SearchIcon from 'svg/SearchIcon';
import EmptyState from 'screens/PlayerLoggedInScreen/components/EmptyState';
import CardsSection from 'components/CardSection';
import TournamentCard from 'components/Explore/Tournaments/TournamentCard';
import { formatDateRange } from 'components/Explore/Tournaments/utils';
import { resolveVenueTimeZone } from 'utils/shared/time/venueDayFilters';
import IconActionLink from 'components/IconActionLink';
import Link from 'components/Link';
import { getEventLocation } from 'utils/shared/location/getEventLocation';
import classNames from 'styles/utils/classNames';
import EventTypeModal from './EventTypeModal';
import { HostedEventsProps } from './props';

const MemoizedCard = memo(TournamentCard);

type HostedEvent = GetHostedEventsByUserIdQuery['events'][0] & {
  startTimestamp: number;
  displayDate: string;
  distance: number;
};

function decorateEvents(
  events: GetHostedEventsByUserIdQuery['events'],
  position: Position | null | undefined,
): HostedEvent[] {
  return (events || []).map((event) => {
    const distance =
      position && event.latitude != null && event.longitude != null
        ? Math.round(
            calculateHaversineDistance({
              coord1: { latitude: position.latitude, longitude: position.longitude },
              coord2: { latitude: event.latitude, longitude: event.longitude },
              unit: 'miles',
            }),
          )
        : 0;
    return {
      ...event,
      startTimestamp: new Date(event.startDateTime).getTime(),
      displayDate: formatDateRange(
      new Date(event.startDateTime),
      new Date(event.endDateTime),
      resolveVenueTimeZone(event),
    ),
      distance,
    };
  });
}

const DEFAULT_EMPTY_STATE_TEXT = "You don't have any hosted events yet.";

const HostedEvents: React.FC<HostedEventsProps> = ({
  events,
  loading,
  position,
  createEventHref = TOURNAMENT_QUICK_CREATE_PAGE,
  emptyStateText = DEFAULT_EMPTY_STATE_TEXT,
}) => {
  const prevArrowRef = useRef<HTMLDivElement>(null);
  const nextArrowRef = useRef<HTMLDivElement>(null);
  const [listHoveredTournamentId, setListHoveredTournamentId] = useState<string | null>(null);
  const eventTypeModal = useModal();
  const decoratedEvents = decorateEvents(events, position);

  const getArrow = (type: 'prev' | 'next') => {
    const ref = type === 'prev' ? prevArrowRef : nextArrowRef;
    const Icon = type === 'prev' ? ChevronLeft : ChevronRight;
    return (
      <div
        ref={ref}
        className="grid h-10 w-10 place-items-center rounded-full bg-color-bg-lightmode-primary shadow-[0px_4px_16px_0px_rgba(0,0,0,0.08)] dark:bg-color-bg-lightmode-primary"
      >
        <Icon className="h-4 w-4" />
      </div>
    );
  };

  return (
    <div
      className={classNames(
        'container mx-auto mt-[1.6rem] border-b border-color-bg-input-lightmode-primary pb-ds-2xl dark:border-color-bg-input-darkmode-primary',
        decoratedEvents.length > 0 ? 'max-sm:pl-4' : 'max-sm:px-4',
      )}
    >
      <div className="mb-5 flex justify-between gap-2 max-sm:mb-3">
        <div className="flex items-center gap-3">
          <h3 className="typography-product-heading-desktop max-sm:typography-product-heading-mobile">
            Hosted events
          </h3>
          <Link
            href={MY_ACTIVITY_PAGE}
            className="typography-product-button-label-small flex items-center text-color-text-brand"
          >
            View All Activity <ChevronRight className="h-4 w-4" />
          </Link>
        </div>

        <div
          className={classNames(
            'hidden items-center gap-2',
            decoratedEvents.length > 0 && 'sm:!flex',
          )}
        >
          {getArrow('prev')}
          {getArrow('next')}
        </div>
      </div>

      {!loading && decoratedEvents.length === 0 && (
        <EmptyState
          text="You don’t have any hosted events yet."
          onClick={() => eventTypeModal.openModal()}
          title="Create new event"
        />
      )}

      {decoratedEvents.length > 0 && (
        <CardsSection
          loading={loading}
          scrollOffset={{
            base: 0,
            xs: 100,
            lg: 0,
          }}
          visibleCards={{
            base: 1,
            xs: 1,
            md: 2,
            lg: 3,
          }}
          gap="1rem"
          externalPrevArrowRef={prevArrowRef}
          externalNextArrowRef={nextArrowRef}
          data={decoratedEvents}
          cardsWrapperClassName="py-4 px-3 scroll-px-3" // to accommodate hover borders
          renderCard={(tournament) => (
            <Link key={tournament.id} href={getMyTournamentDashboardUrl(tournament.id)}>
              <MemoizedCard
                listHoveredTournamentId={listHoveredTournamentId}
                setListHoveredTournamentId={setListHoveredTournamentId}
                tournamentId={tournament.id}
                coverImageUrl={
                  tournament.coverImagePath
                    ? getImageUrl({
                        url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.coverImagePath}`,
                        path: tournament.coverImagePath,
                      })
                    : ''
                }
                organizerImageUrl={
                  tournament.organizerImagePath
                    ? getImageUrl({
                        url: `${process.env.CLOUDFLARE_PUBLIC_URL}${tournament.organizerImagePath}`,
                        path: tournament.organizerImagePath,
                      })
                    : ''
                }
                title={tournament.title}
                registrationClosedAt={tournament.registrationClosedAt}
                startTimestamp={tournament.startTimestamp}
                status={tournament.status}
                sourceOrganizerTitle={tournament.sourceOrganizerTitle}
                sourceRegistrationCount={tournament.sourceRegistrationCount}
                registrationCount={tournament.registrationsAggregate?.aggregate?.count || 0}
                isExternal={tournament.isExternal}
                displayDate={tournament.displayDate}
                displayLocation={getEventLocation(tournament)}
                distance={tournament.distance}
                showImageAndPeople={true}
              />
            </Link>
          )}
          affix={
            <IconActionLink
              title="Create new event"
              href={createEventHref}
              onClick={() => eventTypeModal.openModal()}
              className="pl-12"
              icon={<PlusPlain className="h-5 w-5 text-color-text-lightmode-invert" />}
            />
          }
        />
      )}
      <EventTypeModal
        isOpen={eventTypeModal.isOpen}
        onClose={eventTypeModal.closeModal}
        onOpen={eventTypeModal.openModal}
      />
    </div>
  );
};

export default HostedEvents;
